import { mdiChevronDown, mdiChevronRight } from '@mdi/js';
import * as React from 'react';

import {
    NestedNode,
    NestedNodeSelectInsightProps,
    NestedNodeSelectInsightState,
    NestedNodeState,
} from './nested-node-select.insight.model';

export class NestedNodeSelectInsight extends React.Component<
    NestedNodeSelectInsightProps,
    NestedNodeSelectInsightState
> {
    constructor(props: NestedNodeSelectInsightProps) {
        super(props);

        this._propsToState();
    }

    private _propsToState(): void {
        const states: { [key: string]: NestedNodeState } = {};
        const mapNodes = (
            nodes: NestedNode[],
            parentKey: string = undefined,
            depth: number = 0,
        ): void => {
            if (!nodes || depth > 1) return null;
            nodes.forEach((e, i) => {
                const key = `${parentKey}-${i}`;
                states[key] = {
                    key,
                    id: e.id,
                    label: e.label,
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    checked:
                        e.checked ||
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        (this.props.selection &&
                            this.props.selection.some((ee) => ee.id === e.id)),
                    disabled: e.disabled,
                    expanded: false,
                };
                if (parentKey) {
                    states[parentKey].expanded =
                        (this.props.standAloneParent &&
                            states[parentKey].checked) ||
                        states[parentKey].expanded ||
                        states[key].checked;
                }
                mapNodes(e.children, key, depth + 1);
            });
        };
        mapNodes(this.props.nodes);
        this.state = { states };
    }

    private _onClick(
        event: React.MouseEvent<any>,
        node: NestedNode,
        key: string,
    ): void {
        event.stopPropagation();
        const canSelect = this.props.canSelect
            ? this.props.canSelect(node, this.state.states[key].checked)
            : true;
        if (!canSelect) {
            event.preventDefault();
        }
    }

    private _onChange(event: React.ChangeEvent<any>, key: string): void {
        const target = event.target as HTMLInputElement;
        const states = { ...this.state.states };
        states[key].checked = target.checked;
        // eslint-disable-next-line no-return-assign
        !this.props.standAloneParent &&
            this._findChildren(key).forEach(
                (e) => (e.checked = states[key].checked),
            );
        const parent = this._findParent(key);
        if (parent) {
            parent.checked =
                (!this.props.standAloneParent &&
                    this._checkSelected(parent.key, 'any')) ||
                (this.props.standAloneParent && parent.checked);
        }
        if (this.props.onChange) {
            const nodesFlatten = this.props.nodes
                .reduce((p, c) => p.concat(c, c.children), [])
                .filter((e) => !!e);
            this.props.onChange({
                selected: Object.values(this.state.states)
                    .filter((e) => e.checked)
                    .map((e) => nodesFlatten.find((ee) => ee.id === e.id))
                    .filter((e) => !!e),
            });
        }
        this.setState({ states });
    }

    private _onExpandClick(key: string): void {
        const states = { ...this.state.states };
        states[key].expanded = !states[key].expanded;
        this.setState({ states });
    }

    private _findParent(key: string): NestedNodeState {
        const parts = key.split('-');
        const lookup = parts.slice(0, parts.length - 1).join('-');
        return Object.keys(this.state.states)
            .filter((e) => e === lookup)
            .map((e) => this.state.states[e])
            .find((e) => !!e);
    }

    private _findChildren(parentKey: string): NestedNodeState[] {
        return Object.keys(this.state.states)
            .filter((e) => e.startsWith(`${parentKey}-`))
            .map((e) => this.state.states[e]);
    }

    private _checkSelected(parentKey: string, which: 'some' | 'any'): boolean {
        const vals = this._findChildren(parentKey);
        const match = vals.filter((e: NestedNodeState) => e.checked);
        const some = match.length > 0 && match.length < vals.length;
        const all = match.length === vals.length;
        return which === 'some' ? some : which === 'any' ? some || all : false;
    }

    render(): React.ReactNode {
        const atLeastOneHasChild = this.props.nodes.some(
            (e) => e.children && e.children.length !== 0,
        );
        const layoutNodes = (
            nodes: NestedNode[],
            parentKey: string = undefined,
            depth: number = 0,
        ): JSX.Element[] => {
            if (!nodes || depth > 1) return null;
            return nodes.map((e, i) => {
                // eslint-disable-next-line array-callback-return
                if (e.hidden) return;
                const key = `${parentKey}-${i}`;
                const rand = `${Math.random()}`;
                return (
                    <div
                        key={i}
                        style={{ paddingLeft: `calc(1.6rem * ${depth})` }}
                    >
                        <div className="d-flex">
                            {atLeastOneHasChild && (
                                <div className="insight-expand">
                                    {depth === 0 &&
                                        e.children &&
                                        e.children.length !== 0 && (
                                            <button
                                                type="button"
                                                className="btn bg-transparent p-0"
                                                tabIndex={-1}
                                                onClick={() =>
                                                    this._onExpandClick(key)
                                                }
                                            >
                                                <div className="d-inline cmps-icon">
                                                    <svg>
                                                        {this.state.states[key]
                                                            .expanded && (
                                                            <path
                                                                d={
                                                                    mdiChevronDown
                                                                }
                                                            ></path>
                                                        )}
                                                        {!this.state.states[key]
                                                            .expanded && (
                                                            <path
                                                                d={
                                                                    mdiChevronRight
                                                                }
                                                            ></path>
                                                        )}
                                                    </svg>
                                                </div>
                                            </button>
                                        )}
                                </div>
                            )}
                            <div className="custom-control custom-checkbox custom-control-inline">
                                <input
                                    type="checkbox"
                                    className="custom-control-input"
                                    id={`layoutNode${key}${rand}`}
                                    checked={this.state.states[key].checked}
                                    disabled={this.state.states[key].disabled}
                                    onClick={(ee) => this._onClick(ee, e, key)}
                                    onChange={(ee) => this._onChange(ee, key)}
                                    ref={(input) => {
                                        if (
                                            depth > 0 ||
                                            !input ||
                                            this.props.standAloneParent
                                        )
                                            return;
                                        input.indeterminate =
                                            this._checkSelected(key, 'some');
                                    }}
                                />
                                <label
                                    className="custom-control-label"
                                    htmlFor={`layoutNode${key}${rand}`}
                                    onClick={(ee) => this._onClick(ee, e, key)}
                                >
                                    {e.label}
                                </label>
                            </div>
                        </div>
                        {this.state.states[key].expanded &&
                            layoutNodes(e.children, key, depth + 1)}
                    </div>
                );
            });
        };
        return (
            <div className="insight-nested-node-select">
                {layoutNodes(this.props.nodes)}
            </div>
        );
    }
}
